import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { generatePDFReport } from '../utils/pdfExport';

// ── Rep Ratings Bar Chart (#17 — cap 10 bars, scroll for more) ───────────────
const RepRatingsChart = ({ ratings }) => {
  if (!ratings || ratings.length === 0) return null;
  const max = 10;
  const refLine = 8;
  const visible = ratings.slice(0, 10);
  const hasMore = ratings.length > 10;
  const BAR_MIN_W = 28;
  return (
    <div style={card}>
      <h3 style={sectionTitle}>Rep-by-Rep Ratings</h3>
      <div style={{ overflowX: hasMore ? 'auto' : 'visible' }}>
        <div style={{ position: 'relative', minWidth: hasMore ? `${ratings.length * (BAR_MIN_W + 6)}px` : 'auto' }}>
          <div style={{
            position: 'absolute', left: 0, right: 0,
            bottom: `${(refLine / max) * 120 + 20}px`,
            borderTop: '1.5px dashed #d97706', zIndex: 1
          }}>
            <span style={{ position: 'absolute', right: 0, top: '-16px', fontSize: '10px', color: '#d97706', fontWeight: '600' }}>8.0</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', height: '120px', position: 'relative', zIndex: 2 }}>
            {(hasMore ? ratings : visible).map((r, i) => {
              const pct = (r / max) * 100;
              const color = r >= 8 ? '#10b981' : r >= 6 ? '#d97706' : '#ef4444';
              return (
                <div key={i} style={{ flex: hasMore ? `0 0 ${BAR_MIN_W}px` : 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <span style={{ fontSize: '10px', color: '#6c757d', fontWeight: '600' }}>{r.toFixed(1)}</span>
                  <div style={{ width: '100%', height: `${pct}%`, backgroundColor: color, borderRadius: '4px 4px 0 0', minHeight: '4px' }} />
                  <span style={{ fontSize: '10px', color: '#adb5bd' }}>{i + 1}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '8px', fontSize: '11px', color: '#adb5bd' }}>
        <span>{hasMore ? `Scroll to see all ${ratings.length} reps` : 'Rep number'}</span>
        <span style={{ color: '#d97706' }}>— 8.0 target</span>
      </div>
    </div>
  );
};

// ── Improved Angle Range Chart (#13) ─────────────────────────────────────────
const AngleRangeChart = ({ angleRange, thresholds }) => {
  if (!angleRange) return null;
  const { min, max, avg } = angleRange;
  const range = max - min || 1;

  const pct = (val) => Math.max(0, Math.min(100, ((val - min) / range) * 100));

  const downPct = thresholds ? pct(thresholds.down) : null;
  const upPct = thresholds ? pct(thresholds.up) : null;
  const avgPct = pct(avg);

  return (
    <div style={card}>
      <h3 style={sectionTitle}>Angle Range</h3>
      <div style={{ position: 'relative', height: '32px', backgroundColor: '#e9ecef', borderRadius: '8px', marginBottom: '16px' }}>
        {/* Working zone (down to up threshold) — #18 opacity 0.5 + label */}
        {downPct !== null && upPct !== null && (
          <div style={{
            position: 'absolute',
            left: `${downPct}%`,
            width: `${upPct - downPct}%`,
            height: '100%',
            backgroundColor: '#3b82f6',
            borderRadius: '4px',
            opacity: 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <span style={{ fontSize: '9px', fontWeight: '700', color: '#1d4ed8', whiteSpace: 'nowrap', userSelect: 'none' }}>Rep zone</span>
          </div>
        )}
        {/* Min marker */}
        <div style={{ position: 'absolute', left: '0%', top: 0, bottom: 0, width: '4px', backgroundColor: '#ef4444', borderRadius: '4px' }} />
        {/* Max marker */}
        <div style={{ position: 'absolute', right: '0%', top: 0, bottom: 0, width: '4px', backgroundColor: '#10b981', borderRadius: '4px' }} />
        {/* Avg marker */}
        <div style={{ position: 'absolute', left: `${avgPct}%`, top: '50%', transform: 'translate(-50%, -50%)', width: '14px', height: '14px', backgroundColor: '#2c3e50', borderRadius: '50%', border: '2px solid white', zIndex: 2 }} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', textAlign: 'center' }}>
        <div style={{ backgroundColor: '#fef2f2', borderRadius: '10px', padding: '10px' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#ef4444' }}>{min.toFixed(1)}°</div>
          <div style={{ fontSize: '11px', color: '#6c757d' }}>Min (bottom)</div>
        </div>
        <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '10px' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#2c3e50' }}>{avg.toFixed(1)}°</div>
          <div style={{ fontSize: '11px', color: '#6c757d' }}>Average</div>
        </div>
        <div style={{ backgroundColor: '#f0fdf4', borderRadius: '10px', padding: '10px' }}>
          <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>{max.toFixed(1)}°</div>
          <div style={{ fontSize: '11px', color: '#6c757d' }}>Max (top)</div>
        </div>
      </div>
      {thresholds && (
        <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f0f4ff', borderRadius: '8px', display: 'flex', justifyContent: 'space-around' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#ef4444' }}>{thresholds.down.toFixed(1)}°</div>
            <div style={{ fontSize: '10px', color: '#6c757d' }}>Down threshold</div>
          </div>
          <div style={{ width: '1px', backgroundColor: '#e9ecef' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{thresholds.up.toFixed(1)}°</div>
            <div style={{ fontSize: '10px', color: '#6c757d' }}>Up threshold</div>
          </div>
          <div style={{ width: '1px', backgroundColor: '#e9ecef' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>{(thresholds.up - thresholds.down).toFixed(1)}°</div>
            <div style={{ fontSize: '10px', color: '#6c757d' }}>Working range</div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Score Ring ────────────────────────────────────────────────────────────────
const ScoreRing = ({ score }) => {
  const pct = Math.min((score / 10) * 100, 100);
  const r = 44;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  const color = score >= 8 ? '#10b981' : score >= 6 ? '#d97706' : '#ef4444';
  return (
    <svg width="110" height="110" viewBox="0 0 110 110">
      <circle cx="55" cy="55" r={r} fill="none" stroke="#e9ecef" strokeWidth="10" />
      <circle cx="55" cy="55" r={r} fill="none" stroke={color} strokeWidth="10"
        strokeDasharray={circ} strokeDashoffset={offset}
        strokeLinecap="round" transform="rotate(-90 55 55)" />
      <text x="55" y="52" textAnchor="middle" fontSize="22" fontWeight="700" fill={color}>{score.toFixed(1)}</text>
      <text x="55" y="68" textAnchor="middle" fontSize="11" fill="#6c757d">/10</text>
    </svg>
  );
};

// ── Per-Rep Table ─────────────────────────────────────────────────────────────
const RepTable = ({ ratings }) => {
  if (!ratings || ratings.length === 0) return null;
  return (
    <div style={card}>
      <h3 style={sectionTitle}>Per-Rep Breakdown</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: '8px' }}>
        {ratings.map((r, i) => {
          const color = r >= 8 ? '#10b981' : r >= 6 ? '#d97706' : '#ef4444';
          const bg = r >= 8 ? '#f0fdf4' : r >= 6 ? '#fef9e6' : '#fef2f2';
          return (
            <div key={i} style={{ backgroundColor: bg, borderRadius: '10px', padding: '10px', textAlign: 'center', border: `1px solid ${color}33` }}>
              <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>Rep {i + 1}</div>
              <div style={{ fontSize: '18px', fontWeight: '700', color }}>{r.toFixed(1)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Dynamic Details ───────────────────────────────────────────────────────────
const DynamicDetails = ({ result }) => {
  const skip = new Set(['total_reps', 'average_rating', 'ratings', 'ratings_per_rep',
    'angle_range', 'thresholds', 'exercise', 'exerciseName', 'timestamp',
    'video_info', 'corrections', 'feedback', 'warnings']);

  const entries = Object.entries(result).filter(([k, v]) => {
    if (skip.has(k)) return false;
    if (v === null || v === undefined) return false;
    if (typeof v === 'object' && !Array.isArray(v)) return Object.keys(v).length > 0;
    if (typeof v === 'number') return true;
    if (typeof v === 'string' && v.trim() !== '') return true;
    if (Array.isArray(v) && v.length > 0 && typeof v[0] !== 'object') return true;
    return false;
  });

  // Always show these base stats
  const baseStats = [
    result.best_rating != null && { label: 'Best Rep', value: result.best_rating.toFixed(2), unit: '/10', color: '#10b981' },
    result.worst_rating != null && { label: 'Worst Rep', value: result.worst_rating.toFixed(2), unit: '/10', color: '#ef4444' },
    result.duration_seconds != null && { label: 'Duration', value: result.duration_seconds.toFixed(1), unit: 's', color: '#3b82f6' },
    result.video_info?.fps != null && { label: 'FPS', value: result.video_info.fps.toFixed(1), unit: '', color: '#6c757d' },
    result.video_info?.frames != null && { label: 'Frames', value: result.video_info.frames, unit: '', color: '#6c757d' },
  ].filter(Boolean);

  const fmtKey = k => k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  return (
    <div style={card}>
      <h3 style={sectionTitle}>Additional Metrics</h3>

      {/* Base stats grid */}
      {baseStats.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))', gap: '8px', marginBottom: entries.length > 0 ? '16px' : 0 }}>
          {baseStats.map(({ label, value, unit, color }) => (
            <div key={label} style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
              <div style={{ fontSize: '17px', fontWeight: '700', color }}>{value}{unit}</div>
              <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '3px' }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Extra fields from specific exercise variations */}
      {entries.length > 0 && (
        <div style={{ display: 'grid', gap: '8px' }}>
          {entries.map(([k, v]) => {
            if (typeof v === 'object' && !Array.isArray(v)) {
              return (
                <div key={k}>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{fmtKey(k)}</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '6px' }}>
                    {Object.entries(v).map(([sk, sv]) => (
                      <div key={sk} style={{ backgroundColor: '#f8f9fa', borderRadius: '8px', padding: '8px', textAlign: 'center' }}>
                        <div style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>
                          {typeof sv === 'number' ? (sv % 1 === 0 ? sv : sv.toFixed(2)) : sv}
                          {sk.includes('angle') || sk.includes('min') || sk.includes('max') || sk.includes('avg') ? '°' : ''}
                        </div>
                        <div style={{ fontSize: '10px', color: '#6c757d', marginTop: '2px' }}>{fmtKey(sk)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }
            if (Array.isArray(v)) {
              return (
                <div key={k} style={{ padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '4px' }}>{fmtKey(k)}</div>
                  <div style={{ fontSize: '13px', color: '#2c3e50' }}>{v.join(', ')}</div>
                </div>
              );
            }
            if (typeof v === 'string') {
              return (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <span style={{ fontSize: '13px', color: '#6c757d' }}>{fmtKey(k)}</span>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#2c3e50' }}>{v}</span>
                </div>
              );
            }
            const n = typeof v === 'number' ? v : parseFloat(v);
            const unit = k.includes('angle') ? '°' : k.includes('duration') || k.includes('time') ? 's' : (k.includes('score') || k.includes('rating')) && n <= 10 ? '/10' : '';
            return (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <span style={{ fontSize: '13px', color: '#6c757d' }}>{fmtKey(k)}</span>
                <span style={{ fontSize: '15px', fontWeight: '600', color: '#2c3e50' }}>{n % 1 === 0 ? n : n.toFixed(2)}{unit}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ── Insights ──────────────────────────────────────────────────────────────────
const InsightsCard = ({ result }) => {
  const feedback = result.feedback || [];
  const corrections = result.corrections || [];
  const warnings = result.warnings || [];
  if (!feedback.length && !corrections.length && !warnings.length) return null;
  return (
    <div style={card}>
      <h3 style={sectionTitle}>Insights</h3>
      {feedback.map((f, i) => (
        <div key={i} style={{ display: 'flex', gap: '10px', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '8px', marginBottom: '6px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: '#10b981', borderRadius: '50%', marginTop: '5px', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#2c3e50' }}>{f}</span>
        </div>
      ))}
      {corrections.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: '10px', padding: '10px', backgroundColor: '#fef9e6', borderRadius: '8px', marginBottom: '6px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: '#d97706', borderRadius: '50%', marginTop: '5px', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#2c3e50' }}>{c}</span>
        </div>
      ))}
      {warnings.map((w, i) => (
        <div key={i} style={{ display: 'flex', gap: '10px', padding: '10px', backgroundColor: '#fef2f2', borderRadius: '8px', marginBottom: '6px' }}>
          <div style={{ width: '8px', height: '8px', backgroundColor: '#ef4444', borderRadius: '50%', marginTop: '5px', flexShrink: 0 }} />
          <span style={{ fontSize: '13px', color: '#2c3e50' }}>{w}</span>
        </div>
      ))}
    </div>
  );
};

// ── SVG Pie Chart ─────────────────────────────────────────────────────────────
const PieChart = ({ slices, size = 120 }) => {
  const r = size / 2 - 10;
  const cx = size / 2, cy = size / 2;
  const total = slices.reduce((s, sl) => s + sl.value, 0);
  if (total === 0) return null;
  // Single slice — SVG arc can't draw 360°, use a circle instead
  if (slices.length === 1) {
    return (
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={cx} cy={cy} r={r} fill={slices[0].color} />
        <circle cx={cx} cy={cy} r={r * 0.45} fill="#ffffff" />
      </svg>
    );
  }
  let cumAngle = -Math.PI / 2;
  const paths = slices.map((sl) => {
    const angle = (sl.value / total) * 2 * Math.PI;
    const x1 = cx + r * Math.cos(cumAngle);
    const y1 = cy + r * Math.sin(cumAngle);
    cumAngle += angle;
    const x2 = cx + r * Math.cos(cumAngle);
    const y2 = cy + r * Math.sin(cumAngle);
    const large = angle > Math.PI ? 1 : 0;
    const d = `M${cx},${cy} L${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} Z`;
    return <path key={sl.label} d={d} fill={sl.color} />;
  });
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={cx} cy={cy} r={r + 2} fill="#f8f9fa" />
      {paths}
      <circle cx={cx} cy={cy} r={r * 0.45} fill="#ffffff" />
    </svg>
  );
};

// ── Pie Charts Card (combined) ────────────────────────────────────────────────
const PieChartsCard = ({ ratings, score }) => {
  if (!ratings || ratings.length === 0) return null;
  const good = ratings.filter(r => r >= 8).length;
  const ok = ratings.filter(r => r >= 6 && r < 8).length;
  const poor = ratings.filter(r => r < 6).length;
  const qualitySlices = [
    { label: 'Good', value: good, color: '#10b981' },
    { label: 'OK', value: ok, color: '#d97706' },
    { label: 'Poor', value: poor, color: '#ef4444' },
  ].filter(s => s.value > 0);
  const scoreColor = score >= 8 ? '#10b981' : score >= 6 ? '#d97706' : '#ef4444';
  const scoreSlices = [
    { label: 'Score', value: score, color: scoreColor },
    { label: 'Remaining', value: Math.max(0, 10 - score), color: '#e9ecef' },
  ];
  const Legend = ({ items }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: 0 }}>
      {items.map(({ label, value, color }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '9px', height: '9px', borderRadius: '50%', backgroundColor: color, flexShrink: 0 }} />
          <span style={{ fontSize: '11px', color: '#6c757d', whiteSpace: 'nowrap' }}>{label}</span>
          <span style={{ fontSize: '12px', fontWeight: '700', color, marginLeft: 'auto', paddingLeft: '6px' }}>{value}</span>
        </div>
      ))}
    </div>
  );
  return (
    <div style={card}>
      <h3 style={sectionTitle}>Performance Charts</h3>
      <div style={{ display: 'flex', gap: '16px', justifyContent: 'space-around', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', flex: '1 1 120px', minWidth: 0 }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d' }}>Rep Quality</span>
          <PieChart slices={qualitySlices} size={100} />
          <Legend items={[
            { label: 'Good (≥8)', value: good, color: '#10b981' },
            { label: 'OK (6–8)', value: ok, color: '#d97706' },
            { label: 'Poor (<6)', value: poor, color: '#ef4444' },
          ]} />
        </div>
        <div style={{ width: '1px', backgroundColor: '#e9ecef', alignSelf: 'stretch' }} />
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', flex: '1 1 120px', minWidth: 0 }}>
          <span style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d' }}>Score Breakdown</span>
          <div style={{ position: 'relative' }}>
            <PieChart slices={scoreSlices} size={100} />
            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: scoreColor }}>{score.toFixed(1)}</div>
              <div style={{ fontSize: '9px', color: '#6c757d' }}>/10</div>
            </div>
          </div>
          <Legend items={[
            { label: 'Achieved', value: score.toFixed(1), color: scoreColor },
            { label: 'Remaining', value: Math.max(0, 10 - score).toFixed(1), color: '#adb5bd' },
          ]} />
        </div>
      </div>
    </div>
  );
};

// ── Exercise-specific stat cards ─────────────────────────────────────────────
const ExerciseStats = ({ result, exerciseName }) => {
  const ex = exerciseName.toLowerCase();
  const StatPill = ({ label, value, color = '#2c3e50', unit = '' }) => (
    <div style={{ backgroundColor: '#f8f9fa', borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
      <div style={{ fontSize: '20px', fontWeight: '700', color }}>{value}{unit}</div>
      <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '3px' }}>{label}</div>
    </div>
  );

  // Hold exercises
  if (result.total_hold_seconds != null) {
    return (
      <div style={card}>
        <h3 style={sectionTitle}>Hold Stats</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px,1fr))', gap: '8px' }}>
          <StatPill label="Hold Time" value={result.total_hold_seconds.toFixed(1)} unit="s" color="#3b82f6" />
          {result.average_stability != null && <StatPill label="Stability" value={result.average_stability.toFixed(1)} unit="/10" color="#10b981" />}
          {result.average_hip_drop != null && <StatPill label="Hip Drop" value={result.average_hip_drop.toFixed(1)} color={result.average_hip_drop < 5 ? '#10b981' : '#ef4444'} />}
        </div>
      </div>
    );
  }

  // Bilateral exercises (left/right split)
  const hasLeftRight = result.left_reps != null || result.left_steps != null || result.left_kicks != null || result.left_taps != null;
  if (hasLeftRight) {
    const left = result.left_reps ?? result.left_steps ?? result.left_kicks ?? result.left_taps ?? 0;
    const right = result.right_reps ?? result.right_steps ?? result.right_kicks ?? result.right_taps ?? 0;
    const total = left + right;
    const balance = total > 0 ? Math.round((Math.min(left, right) / Math.max(left, right)) * 100) : 100;
    const balColor = balance >= 90 ? '#10b981' : balance >= 75 ? '#d97706' : '#ef4444';
    return (
      <div style={card}>
        <h3 style={sectionTitle}>Left / Right Balance</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px', marginBottom: '12px' }}>
          <StatPill label="Left" value={left} color="#3b82f6" />
          <StatPill label="Right" value={right} color="#8b5cf6" />
          <StatPill label="Balance" value={`${balance}%`} color={balColor} />
        </div>
        {/* Balance bar */}
        <div style={{ height: '8px', backgroundColor: '#e9ecef', borderRadius: '4px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: `${total > 0 ? (left / total) * 100 : 50}%`, backgroundColor: '#3b82f6', borderRadius: '4px' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px', fontSize: '10px', color: '#adb5bd' }}>
          <span>Left</span><span>Right</span>
        </div>
        {result.cadence != null && (
          <div style={{ marginTop: '12px', padding: '10px', backgroundColor: '#f0f4ff', borderRadius: '8px', textAlign: 'center' }}>
            <span style={{ fontSize: '18px', fontWeight: '700', color: '#3b82f6' }}>{result.cadence}</span>
            <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '4px' }}>reps/min</span>
          </div>
        )}
      </div>
    );
  }

  // Jump/plyometric exercises
  if (ex.includes('jump') || ex.includes('bound') || ex.includes('skater') || ex.includes('tuck')) {
    return (
      <div style={card}>
        <h3 style={sectionTitle}>Jump Stats</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px,1fr))', gap: '8px' }}>
          {result.best_rating != null && <StatPill label="Best Jump" value={result.best_rating.toFixed(1)} unit="/10" color="#10b981" />}
          {result.worst_rating != null && <StatPill label="Worst Jump" value={result.worst_rating.toFixed(1)} unit="/10" color="#ef4444" />}
          {result.duration_seconds != null && <StatPill label="Duration" value={result.duration_seconds.toFixed(1)} unit="s" color="#3b82f6" />}
        </div>
      </div>
    );
  }

  // Assessment exercises (no reps, just scores)
  if (result.total_reps === 0 && (result.left_ankle_score != null || result.left_rotation_score != null || result.left_shoulder_score != null || result.depth_score != null)) {
    const scoreItems = [
      result.left_ankle_score != null && { label: 'Left Ankle', value: result.left_ankle_score, unit: '/10' },
      result.right_ankle_score != null && { label: 'Right Ankle', value: result.right_ankle_score, unit: '/10' },
      result.left_rotation_score != null && { label: 'Left Rotation', value: result.left_rotation_score, unit: '/10' },
      result.right_rotation_score != null && { label: 'Right Rotation', value: result.right_rotation_score, unit: '/10' },
      result.left_shoulder_score != null && { label: 'Left Shoulder', value: result.left_shoulder_score, unit: '/10' },
      result.right_shoulder_score != null && { label: 'Right Shoulder', value: result.right_shoulder_score, unit: '/10' },
      result.depth_score != null && { label: 'Depth', value: result.depth_score, unit: '/10' },
      result.shoulder_score != null && { label: 'Shoulder', value: result.shoulder_score, unit: '/10' },
      result.torso_score != null && { label: 'Torso', value: result.torso_score, unit: '/10' },
    ].filter(Boolean);
    return (
      <div style={card}>
        <h3 style={sectionTitle}>Assessment Scores</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px,1fr))', gap: '8px' }}>
          {scoreItems.map(({ label, value, unit }) => {
            const color = value >= 8 ? '#10b981' : value >= 6 ? '#d97706' : '#ef4444';
            return <StatPill key={label} label={label} value={value.toFixed(1)} unit={unit} color={color} />;
          })}
        </div>
      </div>
    );
  }

  // Default rep-based: show best/worst/cadence if available
  const items = [
    result.best_rating != null && { label: 'Best Rep', value: result.best_rating.toFixed(1), unit: '/10', color: '#10b981' },
    result.worst_rating != null && { label: 'Worst Rep', value: result.worst_rating.toFixed(1), unit: '/10', color: '#ef4444' },
    result.cadence != null && { label: 'Cadence', value: result.cadence, unit: '/min', color: '#3b82f6' },
    result.average_back_arch != null && { label: 'Back Arch', value: result.average_back_arch.toFixed(1), color: result.average_back_arch < 3 ? '#10b981' : '#d97706' },
    result.balance_score != null && { label: 'Balance', value: `${Math.round(result.balance_score * 100)}%`, color: result.balance_score >= 0.9 ? '#10b981' : '#d97706' },
  ].filter(Boolean);
  if (items.length === 0) return null;
  return (
    <div style={card}>
      <h3 style={sectionTitle}>Performance Stats</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px,1fr))', gap: '8px' }}>
        {items.map(({ label, value, unit = '', color }) => <StatPill key={label} label={label} value={value} unit={unit} color={color} />)}
      </div>
    </div>
  );
};

const card = { backgroundColor: '#ffffff', borderRadius: '16px', padding: '20px', marginBottom: '16px', border: '1px solid #e9ecef' };
const sectionTitle = { fontSize: '15px', fontWeight: '600', color: '#2c3e50', marginBottom: '16px', marginTop: 0 };

export default function AnalysisResultScreen() {
  const location = useLocation();
  const navigate = useNavigate();

  // Tab persistence across back navigation (#12)
  const [activeTab, setActiveTab] = useState(
    sessionStorage.getItem('resultTab') || 'overview'
  );
  const switchTab = (tab) => {
    sessionStorage.setItem('resultTab', tab);
    setActiveTab(tab);
  };

  // Support both navigation shapes
  const state = location.state || {};
  const video = state.video || {
    videoName: state.exerciseName || 'Video',
    exerciseName: state.exerciseName || 'Exercise',
    result: state.result || null
  };

  if (!video || !video.result) {
    return (
      <div style={{ minHeight: '100vh', padding: '40px 20px', backgroundColor: '#f5f7fa' }}>
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#ffffff', borderRadius: '20px', border: '1px solid #e9ecef' }}>
          <p style={{ fontSize: '15px', color: '#6c757d', marginBottom: '20px' }}>No results found. Please analyze a video first.</p>
          <button onClick={() => navigate('/')} style={{ padding: '12px 24px', backgroundColor: '#2c3e50', color: '#ffffff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
            Start Over
          </button>
        </div>
      </div>
    );
  }

  const result = video.result;
  const score = typeof result.average_rating === 'number' ? result.average_rating : parseFloat(result.average_rating) || 0;
  const reps = result.total_reps ?? 0;
  const ratings = result.ratings || result.ratings_per_rep || [];
  const duration = result.duration_seconds;
  const exerciseName = video.exerciseName || result.exerciseName || result.exercise || 'Exercise';
  const HOLD_EXERCISES = new Set(['plank', 'side plank', 'side_plank']);
  const exLower = exerciseName.toLowerCase();
  const isHoldExercise = HOLD_EXERCISES.has(exLower) || result.total_hold_seconds != null;
  const isAssessment = isHoldExercise || (reps === 0 && (exLower.includes('test') || exLower.includes('assessment') || exLower.includes('drill')));
  const scoreColor = score >= 8 ? '#10b981' : score >= 6 ? '#d97706' : '#ef4444';

  // PDF export — only pass result data, not the File object (#11)
  const handlePDF = () => {
    const safeVideo = { videoName: video.videoName || exerciseName, exerciseName };
    generatePDFReport(safeVideo, result, score, reps, null, exerciseName);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Header — back goes to home (#15) */}
      <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={() => navigate('/')} style={{ width: '40px', height: '40px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '12px', fontSize: '20px', cursor: 'pointer', color: '#2c3e50', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          ←
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ fontSize: '11px', color: '#6c757d', margin: 0, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Analysis Complete</p>
          <h2 style={{ fontSize: '16px', fontWeight: '600', color: '#2c3e50', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exerciseName}</h2>
        </div>
        <button onClick={handlePDF} style={{ padding: '8px 14px', background: '#f8f9fa', border: '1px solid #e9ecef', borderRadius: '10px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', color: '#2c3e50', flexShrink: 0 }}>
          PDF
        </button>
      </div>

      <div style={{ padding: '20px' }}>
        {/* Score Card — #15 fixed 2-col grid: ring left, stats right */}
        <div style={{ ...card, display: 'grid', gridTemplateColumns: '110px 1fr', alignItems: 'center', gap: '16px' }}>
          <ScoreRing score={score} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#2c3e50', lineHeight: 1 }}>
                {isHoldExercise
                  ? `${(result.total_hold_seconds ?? 0).toFixed(1)}s`
                  : isAssessment
                  ? score.toFixed(1)
                  : reps}
              </div>
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>
                {isHoldExercise ? 'Hold Time' : isAssessment ? 'Assessment Score' : 'Total Reps'}
              </div>
            </div>
            {duration != null && (
              <div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: '#2c3e50', lineHeight: 1 }}>{duration.toFixed(1)}s</div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>Duration</div>
              </div>
            )}
            {ratings.length > 0 && (
              <div>
                <div style={{ fontSize: '28px', fontWeight: '700', color: scoreColor, lineHeight: 1 }}>{Math.max(...ratings).toFixed(1)}</div>
                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>Best Rep</div>
              </div>
            )}
          </div>
        </div>

        {/* Tab Bar — #16 descriptive labels */}
        <div style={{ display: 'flex', backgroundColor: '#ffffff', borderRadius: '12px', padding: '4px', marginBottom: '16px', border: '1px solid #e9ecef', gap: '4px' }}>
          {[{ id: 'overview', label: '📊 Charts' }, { id: 'reps', label: '🔁 Per Rep' }, { id: 'details', label: '🗂 Raw Data' }].map(({ id, label }) => (
            <button key={id} onClick={() => switchTab(id)}
              style={{ flex: 1, padding: '10px', border: 'none', borderRadius: '8px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', backgroundColor: activeTab === id ? '#2c3e50' : 'transparent', color: activeTab === id ? '#ffffff' : '#6c757d', transition: 'all 0.2s', whiteSpace: 'nowrap' }}>
              {label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            <ExerciseStats result={result} exerciseName={exerciseName} />
            <RepRatingsChart ratings={ratings} />
            <AngleRangeChart angleRange={result.angle_range} thresholds={result.thresholds} />
            <InsightsCard result={result} />
          </>
        )}

        {/* Reps Tab */}
        {activeTab === 'reps' && (
          <>
            <RepTable ratings={ratings} />
            {ratings.length > 0 && (
              <>
                <div style={card}>
                  <h3 style={sectionTitle}>Rating Summary</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                    {[
                      { label: 'Average', value: (ratings.reduce((a, b) => a + b, 0) / ratings.length).toFixed(2), color: scoreColor },
                      { label: 'Best', value: Math.max(...ratings).toFixed(2), color: '#10b981' },
                      { label: 'Worst', value: Math.min(...ratings).toFixed(2), color: '#ef4444' },
                    ].map(({ label, value, color }) => (
                      <div key={label} style={{ textAlign: 'center', backgroundColor: '#f8f9fa', borderRadius: '12px', padding: '16px' }}>
                        <div style={{ fontSize: '22px', fontWeight: '700', color }}>{value}</div>
                        <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '4px' }}>{label}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <PieChartsCard ratings={ratings} score={score} />
              </>
            )}
          </>
        )}

        {/* Details Tab */}
        {activeTab === 'details' && <DynamicDetails result={result} />}
      </div>

      {/* Bottom Actions — #19 primary/secondary hierarchy + #20 copy summary */}
      <div style={{ position: 'sticky', bottom: 0, padding: '16px', backgroundColor: '#ffffff', borderTop: '1px solid #e9ecef', boxShadow: '0 -4px 16px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <button onClick={() => navigate('/')} style={{ flex: 1, padding: '14px', backgroundColor: '#2c3e50', color: '#ffffff', border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            New Analysis
          </button>
          <button
            onClick={() => {
              const summary = isHoldExercise
                ? `${exerciseName}: ${score.toFixed(1)}/10 — ${(result.total_hold_seconds ?? 0).toFixed(1)}s hold${duration != null ? ` in ${duration.toFixed(1)}s` : ''}`
                : `${exerciseName}: ${score.toFixed(1)}/10 — ${reps} reps${duration != null ? ` in ${duration.toFixed(1)}s` : ''}`;
              navigator.clipboard?.writeText(summary);
            }}
            style={{ padding: '14px 16px', backgroundColor: '#f8f9fa', color: '#2c3e50', border: '1px solid #e9ecef', borderRadius: '12px', fontSize: '13px', cursor: 'pointer', flexShrink: 0 }}
            title="Copy summary">
            📋
          </button>
        </div>
        <button onClick={() => navigate('/overview', { state: { videos: [] } })} style={{ width: '100%', padding: '8px', backgroundColor: 'transparent', color: '#6c757d', border: 'none', fontSize: '13px', cursor: 'pointer', textDecoration: 'underline' }}>
          View Overview
        </button>
      </div>
    </div>
  );
}
